// src/components/ServiceNow/TaskCard.jsx
import React, { useState } from "react";
import {
  Card,
  CardContent,
  Box,
  Typography,
  Checkbox,
  Chip,
  IconButton,
  Collapse,
  Avatar,
  Button,
  Select,
  MenuItem,
  TextField,
  FormControlLabel,
  Menu,
  ListItemIcon,
  ListItemText,
  Alert,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  Schedule as ScheduleIcon,
  Circle as CircleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  PlayArrow as PlayArrowIcon,
  HourglassEmpty as HourglassEmptyIcon,
  FlashOn as FlashOnIcon,
} from "@mui/icons-material";
import {
  getDisplayValue,
  getValue,
  getStateLabel,
  getStateColor,
  getPriorityInfo,
  formatDate,
  calculateDaysUntilDue,
  isTaskClosed,
  isTaskAssigned,
  getUserInitials,
  getUserDisplayName,
  getGroupDisplayName,
} from "../../utils/serviceNowUtils";
import { useDispatch } from "react-redux";
import { SnackBarStoreAction } from "Store/SnackBar";
import { searchUsers } from "services/userServices";

const TaskCard = ({
  task,
  isSelected,
  onSelection,
  onUpdate,
  onAddComment,
  taskStates,
  forceExpanded,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [selectedState, setSelectedState] = useState("");
  const [comment, setComment] = useState("");
  const [isWorkNote, setIsWorkNote] = useState(false);
  const [quickActionsAnchor, setQuickActionsAnchor] = useState(null);
  const [assignToSearch, setAssignToSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [userOptions, setUserOptions] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);

  const dispatch = useDispatch();

  const showSnack = (message, severity) => {
    const obj = {
      open: true,
      message: message,
      severity: severity,
    };
    dispatch(SnackBarStoreAction.updateSnack(obj));
  };

  // Sync local expanded state with parent's forceExpanded
  React.useEffect(() => {
    setExpanded(forceExpanded);
  }, [forceExpanded]);

  // Extract task data
  const taskNumber = getValue(task.number);
  const taskSysId = getValue(task.sys_id);
  const state = getValue(task.state);
  const stateLabel = getStateLabel(state, taskStates);
  const stateColor = getStateColor(state);
  const shortDescription = getDisplayValue(task.short_description);
  const assignedTo = getUserDisplayName(task.assigned_to);
  const assignedToValue = getValue(task.assigned_to);
  const assignmentGroup = getGroupDisplayName(task.assignment_group);
  const priority = getValue(task.priority);
  const priorityInfo = getPriorityInfo(priority);
  const dueDate = getValue(task.due_date);
  const dueDateInfo = calculateDaysUntilDue(dueDate);
  const openedAt = getDisplayValue(task.opened_at);
  const closedAt = getDisplayValue(task.closed_at);
  const closeNotes = getDisplayValue(task.close_notes);
  const comments = getDisplayValue(task.comments);
  const workNotes = getDisplayValue(task.work_notes);
  const updatedBy =
    getUserDisplayName(task.sys_updated_by) ||
    getDisplayValue(task.sys_updated_by);

  const isClosed = isTaskClosed(state);
  const isAssigned = isTaskAssigned(task.assigned_to);
  const isOverdue = dueDateInfo?.isOverdue && !isClosed;

  // Quick Actions handlers
  const handleQuickActionsClick = (event) => {
    setQuickActionsAnchor(event.currentTarget);
  };

  const handleQuickActionsClose = () => {
    setQuickActionsAnchor(null);
  };

  // Handle checkbox selection
  const handleCheckboxChange = (event) => {
    onSelection(taskSysId, event.target.checked);
  };

  // Extract validation logic for closing tasks
  const validateTaskClosing = (selectedState, isAssigned, comment) => {
    if (selectedState !== "3" && selectedState !== "4") {
      return { isValid: true };
    }

    if (!isAssigned) {
      return {
        isValid: false,
        error: "Cannot close task: Task must be assigned before closing",
      };
    }

    if (!comment.trim()) {
      return {
        isValid: false,
        error:
          "Please add a comment or work note in the text area below before closing the task",
      };
    }

    return { isValid: true };
  };

  // Extract logic for building close update data
  const buildCloseUpdateData = (selectedState, comment, isWorkNote) => {
    const updateData = {};

    if (isWorkNote) {
      updateData.work_notes = comment.trim();
    } else {
      updateData.comments = comment.trim();
    }

    updateData.close_notes =
      selectedState === "3"
        ? "Task completed successfully via ASTT"
        : "Task closed incomplete via ASTT";

    return updateData;
  };

  // Refactored handleStatusUpdate function
  const handleStatusUpdate = async () => {
    if (!selectedState) {
      return;
    }

    try {
      const updateData = { state: selectedState };

      // Validate if closing task
      const validation = validateTaskClosing(
        selectedState,
        isAssigned,
        comment
      );
      if (!validation.isValid) {
        showSnack(
          validation.error,
          validation.error.includes("must be assigned") ? "error" : "warning"
        );
        return;
      }

      // If closing and validation passed, build close data
      if (selectedState === "3" || selectedState === "4") {
        const closeData = buildCloseUpdateData(
          selectedState,
          comment,
          isWorkNote
        );
        Object.assign(updateData, closeData);

        setComment("");
        setIsWorkNote(false);
      }

      await onUpdate(taskSysId, updateData);
      setSelectedState("");
    } catch (err) {
      console.error("Error updating status:", err);
    }
  };

  // Handle add comment with validation
  const handleAddComment = async () => {
    if (!comment.trim()) {
      showSnack("Please enter a comment", "warning");
      return;
    }

    try {
      await onAddComment(taskSysId, comment, isWorkNote);
      setComment("");
      setIsWorkNote(false);
    } catch (err) {
      console.error("Error adding comment:", err);
    }
  };

  // Handle user search
  const handleUserSearch = async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      setUserOptions([]);
      return;
    }

    try {
      setUserSearchLoading(true);
      const users = await searchUsers(searchQuery);

      if (users && Array.isArray(users)) {
        setUserOptions(users);
      } else {
        setUserOptions([]);
      }
    } catch (err) {
      console.error("Error searching users:", err);
      setUserOptions([]);
    } finally {
      setUserSearchLoading(false);
    }
  };

  // Handle assign task
  const handleAssignTask = async () => {
    if (!selectedUser || !selectedUser.sys_id) {
      showSnack("Please select a user", "warning");
      return;
    }

    try {
      const updateData = {
        assigned_to: selectedUser.sys_id,
        comments: `Task assigned via ASTT to ${
          selectedUser.name || selectedUser.user_name
        }`,
      };

      await onUpdate(taskSysId, updateData);
      setSelectedUser(null);
      setAssignToSearch("");
      setUserOptions([]);
      showSnack(
        `Task assigned to ${selectedUser.name || selectedUser.user_name}`,
        "success"
      );
    } catch (err) {
      console.error("Error assigning task:", err);
      showSnack("Failed to assign task", "error");
    }
  };

  // Quick Actions with validation
  const handleQuickAction = async (action) => {
    handleQuickActionsClose();

    // Validate assignment before closing
    if ((action === "complete" || action === "incomplete") && !isAssigned) {
      showSnack("Cannot close task: Task must be assigned first", "error");
      return;
    }

    try {
      switch (action) {
        case "complete":
          await onUpdate(taskSysId, {
            state: "3",
            comments:
              "Credentials verified and activated successfully via ASTT",
            work_notes: "All credentials verified and tested",
            close_notes: "All credentials verified",
          });
          break;
        case "awaiting":
          await onUpdate(taskSysId, {
            state: "-5",
            comments: "Awaiting manager approval via ASTT",
          });
          break;
        case "incomplete":
          await onUpdate(taskSysId, {
            state: "4",
            close_notes: "Unable to complete - employee not found in system",
            work_notes: "Employee record not found during processing",
            comments: "Closed incomplete via ASTT",
          });
          break;
        case "progress":
          await onUpdate(taskSysId, {
            state: "2",
            comments: "Started processing via ASTT",
          });
          break;
        default:
          break;
      }
    } catch (err) {
      console.error("Quick action error:", err);
    }
  };

  // Determine border color
  const getBorderColor = () => {
    if (isOverdue) return "#EF5350";
    if (isClosed && state === "3") return "#66BB6A";
    if (state === "2") return "#3299FF";
    if (!isAssigned) return "#FFA726";
    return "#FFC107";
  };

  return (
    <Card
      sx={{
        borderLeft: `4px solid ${getBorderColor()}`,
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        backgroundColor: isSelected ? "#eff6ff" : "white",
        transition: "all 0.2s",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
        },
      }}
    >
      <CardContent>
        {/* Task Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Box
            sx={{ display: "flex", alignItems: "flex-start", gap: 2, flex: 1 }}
          >
            <Checkbox
              checked={isSelected}
              onChange={handleCheckboxChange}
              sx={{
                mt: 0.5,
                color: "#3299FF",
                "&.Mui-checked": { color: "#3299FF" },
              }}
            />

            <Box sx={{ flex: 1 }}>
              {/* Task Number and Status */}
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: "#1a1a1a",
                    fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                  }}
                >
                  {taskNumber}
                </Typography>

                {isOverdue && (
                  <Chip
                    label="OVERDUE"
                    size="small"
                    sx={{
                      backgroundColor: "#EF5350",
                      color: "white",
                      fontWeight: 600,
                      animation: "pulse 2s infinite",
                    }}
                  />
                )}

                <Chip
                  label={stateLabel}
                  size="small"
                  sx={{
                    backgroundColor: `${stateColor}20`,
                    color: stateColor,
                    fontWeight: 600,
                    fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                  }}
                />
              </Box>

              {/* Task Description */}
              <Typography
                variant="body2"
                sx={{
                  color: "#555",
                  mb: 1.5,
                  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                }}
              >
                {shortDescription}
              </Typography>

              {/* Task Metadata */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 2,
                  fontSize: "0.875rem",
                  color: "#666",
                  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                }}
              >
                {/* Assigned To */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  {isAssigned ? (
                    <>
                      <Avatar
                        sx={{
                          width: 24,
                          height: 24,
                          fontSize: "0.75rem",
                          backgroundColor: "#3299FF",
                        }}
                      >
                        {getUserInitials(assignedTo)}
                      </Avatar>
                      <Typography variant="body2">
                        {assignedTo || "Assigned"}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <PersonIcon sx={{ fontSize: 16, color: "#FFA726" }} />
                      <Typography
                        variant="body2"
                        sx={{ color: "#FFA726", fontWeight: 600 }}
                      >
                        Unassigned
                      </Typography>
                    </>
                  )}
                </Box>

                {/* Assignment Group */}
                {assignmentGroup && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <GroupIcon sx={{ fontSize: 16 }} />
                    <Typography variant="body2">{assignmentGroup}</Typography>
                  </Box>
                )}

                {/* Due Date */}
                {dueDateInfo && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <ScheduleIcon
                      sx={{
                        fontSize: 16,
                        color: isOverdue
                          ? "#EF5350"
                          : dueDateInfo.days <= 2
                          ? "#FFA726"
                          : "#66BB6A",
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        color: isOverdue
                          ? "#EF5350"
                          : dueDateInfo.days <= 2
                          ? "#FFA726"
                          : "#666",
                        fontWeight: isOverdue ? 600 : 400,
                      }}
                    >
                      {dueDateInfo.label}
                    </Typography>
                  </Box>
                )}

                {/* Priority */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <CircleIcon sx={{ fontSize: 8, color: priorityInfo.color }} />
                  <Typography variant="body2">
                    {priorityInfo.label} Priority
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {/* Quick Actions Menu */}
            <Button
              variant="contained"
              size="small"
              onClick={handleQuickActionsClick}
              startIcon={<FlashOnIcon />}
              sx={{
                backgroundColor: "#3299FF",
                "&:hover": { backgroundColor: "#2080EF" },
              }}
            >
              Quick Actions
            </Button>

            <Menu
              anchorEl={quickActionsAnchor}
              open={Boolean(quickActionsAnchor)}
              onClose={handleQuickActionsClose}
              PaperProps={{
                sx: { width: 280 },
              }}
            >
              <Box sx={{ px: 2, py: 1, borderBottom: "1px solid #e5e4e2" }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#999",
                    fontWeight: 600,
                    textTransform: "uppercase",
                  }}
                >
                  Common Actions
                </Typography>
              </Box>

              <MenuItem onClick={() => handleQuickAction("complete")}>
                <ListItemIcon>
                  <CheckCircleIcon sx={{ color: "#66BB6A" }} />
                </ListItemIcon>
                <ListItemText
                  primary="Credentials Verified"
                  secondary="Close Complete + Add verification note"
                  secondaryTypographyProps={{ variant: "caption" }}
                />
              </MenuItem>

              <MenuItem onClick={() => handleQuickAction("awaiting")}>
                <ListItemIcon>
                  <HourglassEmptyIcon sx={{ color: "#FFA726" }} />
                </ListItemIcon>
                <ListItemText
                  primary="Awaiting Manager Approval"
                  secondary="Set status + Add approval note"
                  secondaryTypographyProps={{ variant: "caption" }}
                />
              </MenuItem>

              <MenuItem onClick={() => handleQuickAction("incomplete")}>
                <ListItemIcon>
                  <CancelIcon sx={{ color: "#EF5350" }} />
                </ListItemIcon>
                <ListItemText
                  primary="Employee Not Found"
                  secondary="Close Incomplete + Add explanation"
                  secondaryTypographyProps={{ variant: "caption" }}
                />
              </MenuItem>

              <MenuItem onClick={() => handleQuickAction("progress")}>
                <ListItemIcon>
                  <PlayArrowIcon sx={{ color: "#3299FF" }} />
                </ListItemIcon>
                <ListItemText
                  primary="Started Processing"
                  secondary="Set to Work in Progress"
                  secondaryTypographyProps={{ variant: "caption" }}
                />
              </MenuItem>
            </Menu>

            {/* Expand/Collapse */}
            <IconButton
              onClick={() => setExpanded(!expanded)}
              size="small"
              sx={{
                transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.3s",
              }}
            >
              <ExpandMoreIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Expandable Details */}
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ borderTop: "1px solid #e5e4e2", pt: 3, mt: 2 }}>
            {/* Unassigned Warning */}
            {!isAssigned && (
              <Alert
                severity="warning"
                sx={{
                  mb: 3,
                  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                }}
                icon={<PersonIcon />}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                    fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                  }}
                >
                  This task needs assignment
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#666",
                    display: "block",
                    mb: 1,
                    fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                  }}
                >
                  Assignment Group: {assignmentGroup || "Not set"}
                </Typography>
                <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={userOptions}
                    value={selectedUser}
                    onChange={(event, newValue) => setSelectedUser(newValue)}
                    onInputChange={(event, newInputValue) => {
                      setAssignToSearch(newInputValue);
                      handleUserSearch(newInputValue);
                    }}
                    getOptionLabel={(option) => {
                      if (typeof option === "string") return option;
                      return `${
                        option.name || option.user_name || "Unknown"
                      } (${option.email || option.user_name || ""})`;
                    }}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily:
                                "Urbanist, Verdana, Arial, sans-serif",
                              fontWeight: 600,
                            }}
                          >
                            {option.name || option.user_name}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              fontFamily:
                                "Urbanist, Verdana, Arial, sans-serif",
                              color: "#666",
                            }}
                          >
                            {option.email || option.user_name}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    loading={userSearchLoading}
                    loadingText="Searching users..."
                    noOptionsText={
                      assignToSearch.length < 2
                        ? "Type at least 2 characters to search"
                        : "No users found"
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Search by name or email..."
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {userSearchLoading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                        sx={{
                          "& .MuiInputBase-input": {
                            fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                          },
                        }}
                      />
                    )}
                    sx={{ flex: 1 }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleAssignTask}
                    disabled={!selectedUser || userSearchLoading}
                    sx={{
                      backgroundColor: "#FFA726",
                      "&:hover": { backgroundColor: "#FB8C00" },
                    }}
                  >
                    Assign
                  </Button>
                </Box>
              </Alert>
            )}

            {/* Close Notes (for completed tasks) */}
            {isClosed && closeNotes && (
              <Alert
                severity={state === "3" ? "success" : "error"}
                sx={{
                  mb: 3,
                  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                }}
                icon={state === "3" ? <CheckCircleIcon /> : <CancelIcon />}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                    fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                  }}
                >
                  Close Notes
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#555",
                    fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                  }}
                >
                  {closeNotes}
                </Typography>
              </Alert>
            )}

            {/* Status Update Section */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: "#555",
                  mb: 1,
                  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                }}
              >
                Update Status
              </Typography>

              {/* Show helper text when closing states selected */}
              {(selectedState === "3" || selectedState === "4") &&
                !comment.trim() && (
                  <Alert
                    severity="info"
                    sx={{
                      mb: 2,
                      fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                      }}
                    >
                      To close this task, please add a comment in the "Add
                      Comment" section below, then click "Update Status"
                    </Typography>
                  </Alert>
                )}

              <Box sx={{ display: "flex", gap: 1 }}>
                <Select
                  value={selectedState}
                  onChange={(e) => setSelectedState(e.target.value)}
                  size="small"
                  displayEmpty
                  sx={{ flex: 1 }}
                >
                  <MenuItem value="" disabled>
                    Select new status
                  </MenuItem>
                  {taskStates.map((s) => (
                    <MenuItem key={s.value} value={s.value}>
                      {s.label}
                    </MenuItem>
                  ))}
                </Select>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleStatusUpdate}
                  disabled={!selectedState}
                  sx={{
                    backgroundColor: "#3299FF",
                    fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                    "&:hover": { backgroundColor: "#2080EF" },
                  }}
                >
                  Update Status
                </Button>
              </Box>
            </Box>

            {/* Comment Section */}
            <Box sx={{ borderTop: "1px solid #e5e4e2", pt: 3 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: "#555",
                  mb: 1,
                  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                }}
              >
                Add Comment
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={2}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Type your comment..."
                size="small"
                sx={{ mb: 1 }}
              />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isWorkNote}
                      onChange={(e) => setIsWorkNote(e.target.checked)}
                      size="small"
                      sx={{
                        color: "#3299FF",
                        "&.Mui-checked": { color: "#3299FF" },
                      }}
                    />
                  }
                  label={
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#666",
                        fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                      }}
                    >
                      Add as work note (internal only)
                    </Typography>
                  }
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleAddComment}
                  disabled={
                    !comment.trim() ||
                    selectedState === "3" ||
                    selectedState === "4"
                  }
                  sx={{
                    backgroundColor: "#3299FF",
                    "&:hover": { backgroundColor: "#2080EF" },
                  }}
                >
                  Add Comment
                </Button>
              </Box>
            </Box>

            {/* Activity Log */}
            <Box sx={{ borderTop: "1px solid #e5e4e2", pt: 3, mt: 3 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: "#555",
                  mb: 2,
                  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                }}
              >
                Activity Log
              </Typography>

              {/* Comments Section */}
              {comments && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#999",
                      fontWeight: 600,
                      mb: 1,
                      display: "block",
                      fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                    }}
                  >
                    COMMENTS
                  </Typography>
                  <Box
                    sx={{ backgroundColor: "#f5f5f5", borderRadius: 1, p: 2 }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#555",
                        whiteSpace: "pre-wrap",
                        fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                      }}
                    >
                      {comments}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Work Notes Section */}
              {workNotes && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#999",
                      fontWeight: 600,
                      mb: 1,
                      display: "block",
                      fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                    }}
                  >
                    WORK NOTES (Internal)
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: "#FFF3E0",
                      borderRadius: 1,
                      p: 2,
                      border: "1px solid #FFE0B2",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#555",
                        whiteSpace: "pre-wrap",
                        fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                      }}
                    >
                      {workNotes}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Default Activity Entry */}
              {!comments && !workNotes && (
                <Box sx={{ backgroundColor: "#f5f5f5", borderRadius: 1, p: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: "#1a1a1a",
                        fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                      }}
                    >
                      {formatDate(openedAt)}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#666",
                        fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                      }}
                    >
                      - System
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#555",
                      fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                    }}
                  >
                    Task opened by System
                  </Typography>
                </Box>
              )}

              {/* Closed Info */}
              {isClosed && closedAt && (
                <Box
                  sx={{
                    backgroundColor: "#f5f5f5",
                    borderRadius: 1,
                    p: 2,
                    mt: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: "#1a1a1a",
                        fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                      }}
                    >
                      {formatDate(closedAt)}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#666",
                        fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                      }}
                    >
                      - {assignedTo || "System"}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#555",
                      fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                    }}
                  >
                    Task closed {state === "3" ? "complete" : "incomplete"}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default TaskCard;
